import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { cleCurseurLecture } from '@taki/sdk';
import type { TakiSdk, Pastille, PreferenceConversation } from '@taki/sdk';
import { messageAAcquitter, type PastillesPort, type MessageVue, type PortDeFil } from '@taki/ui';

/** Les listes de conversations que la personne a le droit d'ecouter : sans la porte, les rules refusent l'ecoute. */
export interface PortesDeConversations { canaux: boolean; directs: boolean }

/** Un etat par session de l'hote, partage par le rail et la mascotte. */
export function usePastillesDeLHote(sdk: TakiSdk, uid: string | null, email: string | null, portes: PortesDeConversations): { pastilles: PastillesPort; fil: PortDeFil } {
  const [groupes, setGroupes] = useState<Record<string, string[]>>({});
  const [lus, setLus] = useState<Record<string, Date>>({});
  const [lecturePrete, setLecturePrete] = useState(false), [preferencesPretes, setPreferencesPretes] = useState(false);
  const erreurs = useRef<Record<string, string>>({});
  const [table, setTable] = useState<Record<string, Pastille>>({});
  const [preferences, setPreferences] = useState<PreferenceConversation[]>([]);
  const [arrivees, setArrivees] = useState(0);
  const [erreur, setErreur] = useState<string | null>(null);
  const [ajoutees, setAjoutees] = useState<string[]>([]);
  const depuis = useRef(Date.now());
  const visible = useRef<string | null>(null), affichees = useRef<Record<string, { date: Date; id?: string }>>({});
  const derniers = useRef<Record<string, Pastille>>({});
  const abonnements = useRef(new Map<string, { lu: string; arret: () => void }>());
  const preferencesRef = useRef(preferences); preferencesRef.current = preferences;
  const suivre = useCallback((ref: string) => setAjoutees(l => l.includes(ref) ? l : [...l, ref]), []);
  const marquerAffiche = useCallback((ref: string) => {
    if (visible.current !== ref || document.hidden) return;
    const date = affichees.current[ref];
    if (!date) return;
    const cle = ref.startsWith('conversation:') ? ref.slice(13) : ref;
    void (date.id ? sdk.conversations.lecture.marquerElementAffiche(ref, date.id) : sdk.conversations.lecture.marquerLu(cle, date.date)).catch(e => { erreurs.current['lecture'] = String(e); setErreur(Object.values(erreurs.current).join(' ; ')); });
  }, [sdk]);
  const regarder = useCallback((ref: string | null) => { visible.current = ref; if (ref) marquerAffiche(ref); }, [marquerAffiche]);
  useEffect(() => {
    if (!uid) { setGroupes({}); setTable({}); derniers.current = {}; return; }
    setLecturePrete(false); setPreferencesPretes(!email); erreurs.current = {}; setGroupes({}); setTable({}); setLus({}); setPreferences([]); setErreur(null); derniers.current = {}; affichees.current = {}; depuis.current = Date.now(); setArrivees(0); setAjoutees([]); visible.current = null;
    let vivant = true;
    const groupe = (cle: string, refs: string[]) => { if (vivant) setGroupes(g => ({ ...g, [cle]: refs })); };
    const e = (cause: unknown) => { erreurs.current['sources'] = cause instanceof Error ? cause.message : String(cause); setErreur(Object.values(erreurs.current).join(' ; ')); };
    const arrets = [
      sdk.conversations.observerSuivies(l => groupe('suivies', l.map(c => `conversation:${c.id}`)), e),
      sdk.conversations.preferences.observerEpinglees(l => {
        void Promise.all(l.filter(r => r.startsWith('conversation:')).map(async r => await sdk.conversations.get(r.slice(13)).catch(() => null) ? r : null)).then(refs => groupe('epinglees', refs.flatMap(r => r ? [r] : [])));
      }, e),
      sdk.liaConversations.observer(l => groupe('lia', l.filter(s => s.archiveLe === null).map(s => `lia:${uid}:${s.id}`)), e),
      sdk.liaConversations.observerPartagesAvecMoi(l => groupe('liaPartage', l.filter(s => s.archiveLe === null).map(s => `lia:${s.proprietaireUid}:${s.id}`)), e),
      sdk.conversations.lecture.observer(lu => { setLus(lu); setLecturePrete(true); }, e),
    ];
    if (email) arrets.push(sdk.notifications.conversationsPrefs.observer(p => { setPreferences(p); setPreferencesPretes(true); }, e));
    const reconnexion = () => { depuis.current = Date.now(); };
    window.addEventListener('online', reconnexion);
    const focus = () => { if (visible.current) marquerAffiche(visible.current); };
    window.addEventListener('focus', focus); document.addEventListener('visibilitychange', focus);
    return () => { vivant = false; window.removeEventListener('online', reconnexion); arrets.forEach(a => a()); window.removeEventListener('focus', focus); document.removeEventListener('visibilitychange', focus); };
  }, [sdk, uid, email, marquerAffiche]);
  // Canaux et directs a part : la porte se connait apres la session, et l'ouvrir ne relance pas les autres ecoutes.
  // `email` aussi : l'effet du dessus remet les groupes a zero quand il change.
  const { canaux, directs } = portes;
  useEffect(() => {
    if (!uid) return;
    let vivant = true;
    const groupe = (cle: string, refs: string[]) => { if (vivant) setGroupes(g => ({ ...g, [cle]: refs })); };
    const e = (cause: unknown) => { if (vivant) { erreurs.current['sources'] = cause instanceof Error ? cause.message : String(cause); setErreur(Object.values(erreurs.current).join(' ; ')); } };
    const arrets = [
      ...(canaux ? [sdk.conversations.observerCanaux(l => groupe('canaux', l.map(c => `conversation:${c.id}`)), e)] : []),
      ...(directs ? [sdk.conversations.observerDirects(l => groupe('directs', l.map(c => `conversation:${c.id}`)), e)] : []),
    ];
    return () => { vivant = false; arrets.forEach(a => a()); setGroupes(({ canaux: _c, directs: _d, ...reste }) => reste); };
  }, [sdk, uid, email, canaux, directs]);
  const refs = [...new Set([...Object.values(groupes).flat(), ...ajoutees])].sort().join('|');
  const luCle = Object.entries(lus).map(([k, v]) => `${k}:${cleCurseurLecture(v)}`).sort().join('|');
  useEffect(() => () => { abonnements.current.forEach(a => a.arret()); abonnements.current.clear(); }, [sdk, uid]);
  useEffect(() => {
    if (!uid || !lecturePrete || !preferencesPretes) return;
    const ids = refs ? refs.split('|') : [];
    setTable(t => Object.fromEntries(Object.entries(t).filter(([id]) => ids.includes(id))));
    for (const [ref, a] of abonnements.current) if (!ids.includes(ref)) { a.arret(); abonnements.current.delete(ref); }
    for (const ref of ids) {
      const curseur = lus[ref.startsWith('conversation:') ? ref.slice(13) : ref];
      const lu = cleCurseurLecture(curseur);
      const actuel = abonnements.current.get(ref);
      if (actuel?.lu === lu) continue;
      actuel?.arret();
      let vivant = true;
      const recevoir = (p: Pastille) => {
        if (!vivant) return;
        const ancien = derniers.current[ref]; derniers.current[ref] = p;
        const muette = preferencesRef.current.some(d => d.ref === ref && d.sourdine);
        if (p.motif === 'arrivee' && p.nombre > (ancien?.nombre ?? 0) && !muette) setArrivees(n => n + 1);
        setTable(t => ({ ...t, [ref]: p })); delete erreurs.current[ref]; setErreur(Object.values(erreurs.current).join(' ; ') || null);
      };
      const e = (cause: unknown) => { if (vivant) { erreurs.current[ref] = cause instanceof Error ? cause.message : String(cause); setErreur(Object.values(erreurs.current).join(' ; ')); } };
      const depart = derniers.current[ref] ? undefined : depuis.current;
      const [, proprietaire, sujet] = ref.split(':');
      const arret = ref.startsWith('conversation:')
        ? sdk.conversations.lecture.observerPastilles(ref.slice(13), curseur, recevoir, e, depart)
        : sdk.liaConversations.observerPastilles(proprietaire!, sujet!, curseur, recevoir, e, depart);
      abonnements.current.set(ref, { lu, arret: () => { vivant = false; arret(); } });
    }
  }, [sdk, uid, refs, luCle, lecturePrete, preferencesPretes]);
  const fil = useMemo<PortDeFil>(() => {
    const recevoir = (id: string, callback: (m: MessageVue[], meta?: { local: boolean }) => void) => (messages: MessageVue[], meta?: { local: boolean }) => {
      callback(messages, meta);
      // V3-1 : un rendu qui porte encore un envoi de ce poste (pas ecrit au serveur) n'acquitte rien, quel que soit
      // le fil (direct, canal, chatter de fiche) ; le rendu confirme qui suit pose le curseur, sur un message qui existe.
      const aAcquitter = messageAAcquitter(messages, meta);
      if (aAcquitter === null) return;
      const ref = `conversation:${id}`;
      affichees.current[ref] = aAcquitter;
      requestAnimationFrame(() => marquerAffiche(ref));
    };
    return { ...sdk.messages,
      observerConversation: (id, opts, callback, e) => sdk.messages.observerConversation(id, opts, recevoir(id, callback), e),
      // Un fil partiel ne marque pas les autres messages de la conversation lus.
      observerFil: (id, parent, callback, e) => sdk.messages.observerFil(id, parent, callback, e),
    };
  }, [sdk, marquerAffiche]);
  const pastilles = useMemo<PastillesPort>(() => ({
    parConversation: Object.fromEntries(Object.entries(table).map(([ref, p]) => [ref, p.nombre])),
    dernierAt: Object.fromEntries(Object.entries(table).map(([ref, p]) => [ref, p.dernierAt])),
    total: Object.entries(table).reduce((n, [ref, p]) => n + (preferences.some(d => d.ref === ref && d.sourdine) ? 0 : p.nombre), 0),
    arrivees, erreur, preferences, suivre, regarder, marquerAffiche,
    marquerLiaAffiche: (ref, date, docId) => { affichees.current[ref] = { date, ...(docId ? { id: docId } : {}) }; marquerAffiche(ref); },
  }), [table, arrivees, erreur, preferences, suivre, regarder, marquerAffiche]);
  return { pastilles, fil };
}
